import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';
import { type DeliveryNoteHeader } from './types';

type CreateDeliveryNoteResponse = {
  code: number;
  id: UUID;
  requestId: string;
};

type CreateDeliveryNotePayload = DeliveryNoteHeader;

/**
 * Creates a delivery note.
 *
 * @param {CreateDeliveryNotePayload} deliveryNoteData - An object containing the delivery note details to be created.
 * @returns {Promise<CreateDeliveryNoteResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/create_manual_delivery_note_internal_format_asset_manual_post
 */
export const createDeliveryNote = async (
  deliveryNoteData: CreateDeliveryNotePayload,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.CREATE(), {
      json: deliveryNoteData,
    })
    .json<CreateDeliveryNoteResponse>();

  return response;
};

/**
 * Custom hook for creating a delivery note using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateDeliveryNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deliveryNoteData: CreateDeliveryNotePayload) =>
      createDeliveryNote(deliveryNoteData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.getAll({}),
      });
    },
  });
};
